import { useId, useState } from 'react';

import { Button, Dialog, Field, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { HOP_THAO_TAC, THAO_TAC_TAI_LIEU, TRANG_THAI_TAI_LIEU } from '../constants';
import type { ThaoTacCoHop } from '../constants';
import { useChuyenTaiLieu, usePhienBanMoi } from '../hooks/useTriThuc';
import { trangThaiSau } from '../nghiep-vu/tri-thuc';
import type { KnowledgeDocument } from '../types';

const DAI_GHI_CHU = 1000;
const DAI_GHI_CHU_PHIEN_BAN = 500;
const DAI_TOM_TAT = 20_000;

/**
 * Xác nhận một thao tác chuyển trạng thái tài liệu. Trả nháp nhận ghi chú tuỳ chọn, yêu cầu sửa bắt buộc
 * ghi chú — người soạn đọc lại nó ở tài liệu. Lỗi hiện trong hộp, hộp không đóng.
 */
export function HopThaoTacTaiLieu({
  taiLieu,
  thaoTac,
  open,
  onClose,
}: {
  taiLieu: KnowledgeDocument;
  thaoTac: ThaoTacCoHop;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const chuyen = useChuyenTaiLieu();
  const hop = HOP_THAO_TAC[thaoTac];
  const coGhiChu = thaoTac === 'return-draft' || thaoTac === 'request-revision';
  const batBuocGhiChu = thaoTac === 'request-revision';
  const sau = trangThaiSau(thaoTac, taiLieu.status);

  const [ghiChu, setGhiChu] = useState('');
  const [daGui, setDaGui] = useState(false);
  const [loi, setLoi] = useState<{ loi: unknown; truong: Record<string, string> } | null>(null);

  const loiGhiChu = (daGui && batBuocGhiChu && !ghiChu.trim() ? t('triThuc.taiLieu.loiGhiChuTrong') : undefined)
    ?? loi?.truong['/note'];
  const loiChung = loi && !loi.truong['/note'] ? loi.loi : null;

  const gui = async () => {
    setDaGui(true);
    if ((batBuocGhiChu && !ghiChu.trim()) || chuyen.isPending) return;
    setLoi(null);
    try {
      await chuyen.mutateAsync({ taiLieu, thaoTac, ...(coGhiChu && ghiChu.trim() ? { note: ghiChu.trim() } : {}) });
      onClose();
    } catch (err) {
      setLoi({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(hop.tieuDe)}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={chuyen.isPending}>
              {t(THAO_TAC_TAI_LIEU[thaoTac].nhan)}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="text-sm leading-relaxed text-content-secondary">
          {t(hop.than, {
            ten: taiLieu.title,
            bo: taiLieu.knowledge_base_name,
            trangThai: sau ? t(TRANG_THAI_TAI_LIEU[sau].khoa) : '',
          })}
        </p>
        {coGhiChu && (
          <Field
            label={t('triThuc.taiLieu.fGhiChu')}
            required={batBuocGhiChu}
            hint={batBuocGhiChu ? undefined : t('triThuc.taiLieu.fGhiChuTuyChon')}
            error={loiGhiChu}
          >
            {(p) => (
              <Textarea
                {...p}
                autoFocus
                rows={4}
                maxLength={DAI_GHI_CHU}
                placeholder={t('triThuc.taiLieu.fGhiChuViDu')}
                value={ghiChu}
                onChange={(e) => {
                  setGhiChu(e.target.value);
                  setLoi(null);
                }}
              />
            )}
          </Field>
        )}
      </form>
    </Dialog>
  );
}

/**
 * Tạo phiên bản mới của tài liệu (`POST /documents/{id}/versions`): tăng `vX.Y` và đưa tài liệu về nháp.
 * Backend chưa nhận tệp mới ở bước này — phiên bản chỉ ghi lại thay đổi và tóm tắt.
 */
export function FormPhienBan({ taiLieu, open, onClose }: { taiLieu: KnowledgeDocument; open: boolean; onClose: () => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const tao = usePhienBanMoi();

  const [ghiChu, setGhiChu] = useState('');
  const [tomTat, setTomTat] = useState('');
  const [daGui, setDaGui] = useState(false);
  const [loi, setLoi] = useState<{ loi: unknown; truong: Record<string, string> } | null>(null);

  const truong = loi?.truong ?? {};
  const loiGhiChu = (daGui && !ghiChu.trim() ? t('triThuc.taiLieu.loiThayDoiTrong') : undefined) ?? truong['/change_note'];
  const loiChung = loi && Object.keys(truong).length === 0 ? loi.loi : null;

  const gui = async () => {
    setDaGui(true);
    if (!ghiChu.trim() || tao.isPending) return;
    setLoi(null);
    try {
      await tao.mutateAsync({ taiLieu, change_note: ghiChu.trim(), content_summary: tomTat.trim() || null });
      onClose();
    } catch (err) {
      setLoi({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.taiLieu.phienBanTieuDe', { ten: taiLieu.title })}
      width={520}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={tao.isPending}>
              {tao.isPending ? t('chung.dangLuu') : t('triThuc.taiLieu.taoPhienBan')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="text-xs leading-relaxed text-content-muted">
          {t('triThuc.taiLieu.phienBanMo', { phienBan: taiLieu.current_version })}
        </p>
        <Field label={t('triThuc.taiLieu.fThayDoi')} required error={loiGhiChu}>
          {(p) => (
            <Textarea
              {...p}
              autoFocus
              rows={3}
              maxLength={DAI_GHI_CHU_PHIEN_BAN}
              value={ghiChu}
              onChange={(e) => {
                setGhiChu(e.target.value);
                setLoi(null);
              }}
            />
          )}
        </Field>
        <Field label={t('triThuc.taiLieu.fTomTat')} error={truong['/content_summary']}>
          {(p) => (
            <Textarea
              {...p}
              rows={5}
              maxLength={DAI_TOM_TAT}
              value={tomTat}
              onChange={(e) => {
                setTomTat(e.target.value);
                setLoi(null);
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
